<template>
  <Loading v-if="loading" />
  <Modal v-if="showModalEditSensor" @close="$emit('closeModal')">
    <div class="editSensor">
      <div class="editSensor__header">
        <span class="title">
          {{ $t('editSensor') }}
        </span>
        <img
          class="editSensor__header-img"
          src="@/assets/icons/CloseModal.svg"
          alt="Close Modal"
          @click="$emit('closeModal')"
        />
      </div>
      <FormKit
        id="editSensor-form"
        v-model="formData"
        type="form"
        :actions="false"
        @submit="submitHandler"
      >
        <div class="editSensor__columns">
          <div class="editSensor__column">
            <div
              v-for="(el, index) in infoSensor"
              :key="index"
              class="infoSensor"
            >
              <Input
                :type="el.type"
                :name="el.name"
                :label="$t(el.label)"
                :disabled="el.name === 'sn' || el.name === 'id'"
              />
            </div>
          </div>

          <div class="editSensor__column">
            <div class="infoSensor editSensor__color">
              <p class="editSensor__color-label">{{ $t('colorLabel') }}</p>
              <div class="editSensor__color-row">
                <input
                  v-model="formData.color"
                  type="text"
                  class="editSensor__color-text"
                  placeholder="#000000"
                />
                <input
                  :value="colorPickerValue"
                  type="color"
                  class="editSensor__color-picker"
                  @input="formData.color = $event.target.value"
                />
              </div>
            </div>

            <div class="infoSensor editSensor__type">
              <FormKit
                v-model="formData.type"
                type="select"
                name="type"
                :label="$t('sensorType')"
                :options="sensorTypeOptions"
              />
            </div>

            <div class="infoSensor">
              <Input
                type="number"
                name="max"
                :label="$t('maxUpperCase')"
                :disabled="isTempLocked"
              />
            </div>

            <div class="infoSensor">
              <Input
                type="number"
                name="min"
                :label="$t('minUpperCase')"
                :disabled="isTempLocked"
              />
            </div>
          </div>
        </div>
      </FormKit>
      <div class="editSensor__buttons">
        <Button variant="filled" :title="$t('save')" @click="editSensor" />
        <Button
          variant="bordered"
          :title="$t('cancel')"
          @click="$emit('closeModal')"
        />
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions } from 'vuex';

import Loading from '@/views/Loading.vue';

import {
  infoSensor,
  sensorTypeOptions,
  SENSOR_TYPE_RANGES,
  normalizeSensorData
} from './configs';

@Options({
  name: 'EditSensor',
  emits: ['closeModal'],
  props: ['editSensorData', 'pharmacyId', 'showModalEditSensor'],
  components: {
    Loading
  },
  data() {
    return {
      formData: normalizeSensorData(this.editSensorData),
      loading: false,
      infoSensor,
      sensorTypeOptions: sensorTypeOptions(this.$t)
    };
  },
  computed: {
    isTempLocked(): boolean {
      return this.formData?.type === 'fridge' || this.formData?.type === 'room';
    },
    // Native color input only accepts a valid #rrggbb value
    colorPickerValue(): string {
      const color = this.formData?.color;
      return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000';
    }
  },
  methods: {
    ...mapActions('pharmacy', ['updateSensor']),
    async submitHandler() {
      this.loading = true;

      const payload = {
        name: this.formData.name,
        description: this.formData.description,
        max: +this.formData.max,
        min: +this.formData.min,
        color: this.formData.color,
        type: this.formData.type
      };

      await this.updateSensor({
        data: payload,
        pharmacyId: this.pharmacyId,
        sensorId: this.editSensorData.id
      });

      this.$emit('closeModal');
      this.loading = false;
    },
    editSensor() {
      this.$formkit.submit('editSensor-form');
    }
  },
  watch: {
    editSensorData() {
      if (this.editSensorData) {
        this.formData = normalizeSensorData(this.editSensorData);
      }
    },
    'formData.type'(newType: string) {
      const range = SENSOR_TYPE_RANGES[newType];
      if (range) {
        this.formData.max = range.max;
        this.formData.min = range.min;
      }
    }
  }
})
export default class EditSensor extends Vue {}
</script>

<style lang="scss" scoped>
.editSensor {
  width: 100%;
  height: 100%;
  padding: 24px;
  .editSensor__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 23px;
    .title {
      font-family: 'Ubuntu-Medium';
      font-size: $eighteenSize;
      line-height: $fifteenSize;
    }
    .editSensor__header-img {
      cursor: pointer;
    }
  }
  #editSensor-form {
    padding-top: 24px;
  }
  .editSensor__columns {
    display: flex;
    gap: 24px;
    .editSensor__column {
      flex: 1;
      min-width: 0;
    }
  }
  .editSensor__type {
    margin-bottom: 30px;
  }
  .editSensor__color {
    position: relative;
    margin-bottom: 30px;
    .editSensor__color-label {
      position: absolute;
      font-family: 'Ubuntu-Regular';
      top: -6px;
      left: 15px;
      padding: 0 5px;
      background: $white;
      color: $label;
      font-weight: 400;
      font-size: $twelveSize;
      line-height: $fourteenSize;
      z-index: 1;
    }
    .editSensor__color-row {
      display: flex;
      align-items: center;
      gap: 8px;
      .editSensor__color-text {
        flex: 1;
        min-width: 0;
        height: 48px;
        font-size: 14px;
        border-radius: 8px;
        border: 1px solid $border;
        padding: 0 0 0 15px;
        box-sizing: border-box;
        color: $main-text;
        font-family: 'Ubuntu-Regular';
        outline: none;
        text-transform: uppercase;
        &:focus {
          border: 1px solid $main-green;
        }
      }
      .editSensor__color-picker {
        width: 48px;
        height: 48px;
        flex-shrink: 0;
        padding: 4px;
        border: 1px solid $border;
        border-radius: 8px;
        background: $white;
        cursor: pointer;
      }
    }
  }
  .editSensor__buttons {
    :first-child {
      margin-right: 12px;
    }
  }
}
</style>

<style lang="scss">
.modal-container:has(.editSensor) {
  max-width: 780px !important;
  width: 780px !important;
}
</style>
